// The places lab's view (gate C4), mounted in the valley look's warm-up so every program compiles before ready:
//   - the ground: the lab's height function as faceted, palette-coloured triangles (grass, the paved square, paths,
//     banks and stream beds) on the look's one surface material; a slab of stone under the remote room;
//   - the kit: the town merged into one mesh (with a far form for distance) and the remote room into another, on the
//     kit surface (one program: night-lit windows and lamps, the dithered cut-away);
//   - the water: the stream's and the pond's sheets merged into one mesh on the low-poly water material;
//   - interiors: when the sim's director says the hero is inside a room, the camera asks for the room's profile (on the
//     profile table's layer) and stops colliding with buildings (it looks in over the cut walls), the roof and the walls
//     that face the camera dissolve, and the look blends to the room's indoor light model (the stub until the sky
//     stream's `look.indoor`); leaving reverses all of it. Nothing loads: the room was always there;
//   - doors: a transition's fade is a black layer over the canvas at the director's opacity.
// A view: it reads the sim and never writes it.
import * as THREE from "three";
import { ensureGlow, kitSurface, kitUniforms, lowPolyWater, setKitNight, waterUniforms, type LowPolyLook, type LowPolySceneLook } from "@rpg/look";
import { batchPieces, facet, merge, seeded, waterSurface } from "@rpg/meshkit";
import { waterLevel, waterShapeDistance } from "@rpg/collision";
import type { Runtime } from "@rpg/runtime";
import type { ValleyCamera } from "../valley/camera";
import type { PlacesContent, PlacesLightRow } from "./content";
import { interiorCamera, interiorCut } from "./interior-view";
import { GROUND, type PlacesWorld } from "./layout";
import { blendLooks, indoor, night as nightLook } from "./lab-light";
import type { PlacesSim } from "./module";

/** The view's report. */
export interface PlacesViewState {
  inside: string | null;
  /** The light model in use ("outdoor", "night" or an indoor row). */
  light: string;
  cut: { owner: number; mask: number; fade: number };
  fade: number;
  night: number;
  /** Whether the camera collides with buildings (false inside a room). */
  cameraSolid: boolean;
}

export interface PlacesView {
  root: THREE.Group;
  state(): PlacesViewState;
  /** The meshes: ground, town (an LOD of near and far), remote, water. */
  meshes: { ground: THREE.Mesh; town: THREE.LOD; remote: THREE.Mesh; water: THREE.Mesh };
}

const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

function segDist(px: number, pz: number, a: number[], b: number[]): number {
  const dx = b[0]! - a[0]!;
  const dz = b[1]! - a[1]!;
  const l2 = dx * dx + dz * dz;
  const t = l2 > 0 ? Math.min(1, Math.max(0, ((px - a[0]!) * dx + (pz - a[1]!) * dz) / l2)) : 0;
  return Math.hypot(a[0]! + dx * t - px, a[1]! + dz * t - pz);
}

/** The ground mesh: one faceted triangle pair per heightfield cell, coloured by what is there. */
function groundMesh(w: PlacesWorld, c: PlacesContent, look: LowPolyLook): THREE.Mesh {
  const g = c.look.ground;
  const n = GROUND.cells;
  const H = GROUND.half;
  const step = (2 * H) / n;
  const pos: number[] = [];
  const col: number[] = [];
  const rng = seeded(0x9a55);
  const pal = (k: string): THREE.Color => c.palette(k);
  const grass = g.grass.map(pal);
  const tmp = new THREE.Color();
  const colourAt = (x: number, z: number, y: number): THREE.Color => {
    let wet = -Infinity;
    let level = 0;
    for (const v of w.water) {
      const d = waterShapeDistance(v, x, z);
      if (d > wet) {
        wet = d;
        level = waterLevel(v, x, z);
      }
    }
    if (wet > -0.3 && y < level) return tmp.copy(pal(g.bed));
    if (wet > -2.2) return tmp.copy(pal(g.bank)).lerp(grass[0]!, smooth(1.2, 2.2, -wet) * 0.6);
    if (Math.hypot(x, z) < g.squareRadius) return tmp.copy(pal(g.square));
    for (const p of g.paths) for (let i = 0; i + 1 < p.length; i++) if (segDist(x, z, p[i]!, p[i + 1]!) < g.pathWidth) return tmp.copy(pal(g.path));
    return tmp.copy(grass[Math.floor(rng() * grass.length)]!);
  };
  const at = (ix: number, iz: number): [number, number, number] => {
    const x = -H + ix * step;
    const z = -H + iz * step;
    return [x, w.heights[iz * (n + 1) + ix]!, z];
  };
  const tri = (a: [number, number, number], b: [number, number, number], d: [number, number, number]): void => {
    const cx = (a[0] + b[0] + d[0]) / 3;
    const cy = (a[1] + b[1] + d[1]) / 3;
    const cz = (a[2] + b[2] + d[2]) / 3;
    const k = colourAt(cx, cz, cy);
    const t = 1 + (rng() * 2 - 1) * 0.045;
    for (const v of [a, b, d]) {
      pos.push(v[0], v[1], v[2]);
      col.push(k.r * t, k.g * t, k.b * t);
    }
  };
  for (let iz = 0; iz < n; iz++)
    for (let ix = 0; ix < n; ix++) {
      const p00 = at(ix, iz);
      const p10 = at(ix + 1, iz);
      const p01 = at(ix, iz + 1);
      const p11 = at(ix + 1, iz + 1);
      if ((ix + iz) % 2 === 0) {
        tri(p00, p01, p11);
        tri(p00, p11, p10);
      } else {
        tri(p00, p01, p10);
        tri(p10, p01, p11);
      }
    }
  let geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  geo.computeVertexNormals();
  ensureGlow(geo);
  // the slabs under remote rooms
  const slabs = w.slabs.map((s) => {
    const b = new THREE.BoxGeometry(s.hx * 2, 1, s.hz * 2);
    b.translate(s.x, s.top - 0.5, s.z);
    const f = facet(b, { colour: pal(g.square), tint: 0.04, rng });
    return f;
  });
  if (slabs.length) {
    for (const k of Object.keys(geo.attributes)) if (!["position", "normal", "color", "glow"].includes(k)) geo.deleteAttribute(k);
    geo = merge([geo, ...slabs]);
  }
  const mesh = new THREE.Mesh(geo, look.surface);
  mesh.name = "places:ground";
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  return mesh;
}

/** Mount the places view. */
export function mountPlacesView(o: {
  runtime: Runtime;
  scene: THREE.Scene;
  look: LowPolyLook;
  content: PlacesContent;
  world: PlacesWorld;
  sim: PlacesSim;
  camera: ValleyCamera | null;
  /** The renderer (the meshes are uploaded once at mount). */
  renderer?: THREE.WebGLRenderer;
  /** The lab's night (`?night`): the night row's light and lit windows. */
  night: boolean;
}): PlacesView {
  const { look, content: c, world: w } = o;
  // the sim view's stand-in boxes and sheets give way to the kit, the ground and the water
  for (const name of ["sim-test-scene", "valley-water"]) {
    const x = o.scene.getObjectByName(name);
    if (x) x.visible = false;
  }
  const root = new THREE.Group();
  root.name = "places";
  o.scene.add(root);

  const ground = groundMesh(w, c, look);
  root.add(ground);

  // the kit: the town (near and far forms) and the remote room, one mesh each, one shared material
  const ku = kitUniforms(c.look.litColour);
  const kitMat = kitSurface(look.uniforms, ku);
  const items = (group: "town" | "remote") => w.placed.filter((p) => p.group === group).map((p) => ({ piece: p.piece, place: p.place, owner: p.owner }));
  const townNear = new THREE.Mesh(batchPieces(items("town")).geometry, kitMat);
  townNear.name = "places:town";
  const townFar = new THREE.Mesh(batchPieces(items("town"), { far: true }).geometry, kitMat);
  townFar.name = "places:town-far";
  const town = new THREE.LOD();
  town.name = "places:town-lod";
  town.addLevel(townNear, 0);
  town.addLevel(townFar, 90);
  const remote = new THREE.Mesh(batchPieces(items("remote")).geometry, kitMat);
  remote.name = "places:remote";
  for (const m of [townNear, townFar, remote]) m.castShadow = true;
  root.add(town, remote);

  // the water: every body's sheet merged into one mesh
  const wl = c.look.water;
  const wu = waterUniforms({ height: wl.height, length: wl.length, speed: wl.speed, calmShore: wl.calmShore, foam: wl.foam, foamWidth: wl.foamWidth, foamPulse: wl.foamPulse, directions: [0.4, 2.1, -1.2] });
  const colours = { shallow: c.palette(wl.shallow), deep: c.palette(wl.deep), edge: c.palette(wl.edge) };
  const sheets = w.water.map((v, i) =>
    waterSurface({
      min: { x: v.min.x - wl.margin - 1, z: v.min.z - wl.margin - 1 },
      max: { x: v.max.x + wl.margin + 1, z: v.max.z + wl.margin + 1 },
      cell: wl.cell,
      distance: (x, z) => waterShapeDistance(v, x, z),
      level: (x, z) => waterLevel(v, x, z),
      floor: (x, z) => w.ground(x, z),
      colours,
      deepAt: wl.deepAt,
      edgeWidth: wl.edgeWidth,
      margin: wl.margin,
      seed: 0x3a7e + i,
    }),
  );
  const water = new THREE.Mesh(merge(sheets), lowPolyWater(look.uniforms, wu));
  water.name = "places:water";
  water.castShadow = false;
  water.receiveShadow = true;
  root.add(water);
  look.track(root);
  water.castShadow = false;
  ground.castShadow = false;
  // upload every mesh now, the far town and the remote room too (out of view at boot), so walking anywhere later (in
  // through a door, through to the crypt) puts nothing new on the GPU: a draw of each, unculled, behind the warm-up
  // (in the real scene, so its lights give the same programs the warm-up compiles)
  if (o.renderer && o.camera) {
    const prime = new THREE.Group();
    for (const m of [townFar, remote]) {
      const c = new THREE.Mesh(m.geometry, m.material);
      c.frustumCulled = false;
      prime.add(c);
    }
    for (const m of [ground, townNear, remote, water]) m.frustumCulled = false;
    o.scene.add(prime);
    o.renderer.render(o.scene, o.camera.rig.camera);
    prime.removeFromParent();
    for (const m of [ground, townNear, remote, water]) m.frustumCulled = true;
  }

  // the transition fade: a black layer over the canvas
  const veil = document.createElement("div");
  veil.id = "places-fade";
  veil.setAttribute("data-no-strings", "");
  veil.style.cssText = "position:fixed;inset:0;background:#000;opacity:0;pointer-events:none;z-index:5";
  document.body.appendChild(veil);

  // the light: outdoors (the valley's look, or the lab's night), or a room's indoor model, blended
  const lightRow = (id: string): PlacesLightRow => {
    const r = c.lights[id];
    if (!r) throw new Error(`places: no light "${id}"`);
    return r;
  };
  const outdoor: LowPolySceneLook = o.night ? nightLook(c.scene, lightRow("night")) : c.scene;
  const outdoorNight = o.night ? lightRow("night").night : 0;
  let from = outdoor;
  let to = outdoor;
  let blend = 1;
  let lightId = o.night ? "night" : "outdoor";
  if (o.night) look.setLook(outdoor);

  // the cut-away and the camera (shared with the streamed valley: interior-view.ts)
  let inside: string | null = null;
  const camera = interiorCamera(o.camera);
  const cutAway = interiorCut({ kit: ku, fade: c.look.cutFade, margin: c.look.cutMargin });
  let wTime = 0;
  const enter = (id: string): void => {
    const spec = w.interiors.find((i) => i.id === id)!;
    camera.enter(spec.profile);
    from = blendLooks(from, to, blend);
    to = indoor(outdoor, lightRow(spec.light));
    blend = 0;
    lightId = spec.light;
  };
  const exit = (): void => {
    camera.exit();
    from = blendLooks(from, to, blend);
    to = outdoor;
    blend = 0;
    lightId = o.night ? "night" : "outdoor";
  };

  o.runtime.on(
    "late",
    (f) => {
      if (f.mode === "play" && o.runtime.paused) return;
      const s = o.sim.state().interiors;
      if (s.inside !== inside) {
        if (inside) exit();
        inside = s.inside;
        if (inside) enter(inside);
      }
      // the cut-away: the roof and the walls facing the camera, fading in and out
      const dt = f.realDt;
      const camPos = o.camera ? o.camera.rig.frame().position : { x: 0, z: 0 };
      const room = inside ? w.rooms.get(inside)! : null;
      cutAway.frame(room ? { owner: room.owner, interior: room.piece.interior!, place: room.place } : null, camPos, dt);
      // the light
      if (blend < 1) {
        blend = Math.min(1, blend + dt / Math.max(1e-3, c.look.lightBlend));
        look.setLook(blendLooks(from, to, blend * blend * (3 - 2 * blend)));
      }
      const roomNight = inside ? lightRow(w.interiors.find((i) => i.id === inside)!.light).night : 0;
      setKitNight(ku, Math.min(1, outdoorNight + roomNight));
      // the water's waves (real time) and the door's fade
      wTime += dt;
      wu.uWaterTime.value = wTime;
      veil.style.opacity = String(o.sim.fade());
    },
    { order: -5, name: "places: view" },
  );

  return {
    root,
    meshes: { ground, town, remote, water },
    state: () => ({ inside, light: lightId, cut: cutAway.state(), fade: o.sim.fade(), night: ku.uKitNight.value, cameraSolid: camera.solid() }),
  };
}
